import React, { useState } from 'react';
import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Cancel } from '@mui/icons-material';
import { successToastMessage } from '../CommonMethods/ClientSecretCommonMethods';
import { ThemedCopyIcon } from '../../../../Common/Components/Icons';
import { CardWrapper } from '../../Common/CommonComponents';
import { clientsecretService } from '../ClientSecretService';
import { ClientSecret } from '../Model/ClientSecretesModel';
import CustomButton from '../../../../Common/Components/Controls/InputControls';
import { showToast } from '../../../../lib/toast';
import { useTranslation } from 'react-i18next';

export default function RefreshSecretComponent(
  props: Readonly<{
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    clientId: string;
    customerId: string;
  }>,
) {
  const { t } = useTranslation('clientsecret');

  const [refreshedSecret, setRefreshedSecret] = useState<
    ClientSecret | undefined
  >();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const data = await clientsecretService.refreshSecret(
        props.customerId,
        props.clientId,
      );
      setRefreshedSecret(data);
      showToast({
        type: 'success',
        title: t('clientsecret.success.refreshed_secret'),
      });
    } catch (error) {
      console.error(error);
      showToast({
        type: 'error',
        title: t('clientsecret.error.refresh_failed'),
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClose = () => {
    props.setOpen(false);
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="refresh-secret-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="refresh-secret-dialog-title">
        {t('clientsecret.dialog.refresh_title')}
      </DialogTitle>
      <Divider />

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CardWrapper>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  {!refreshedSecret && (
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      {t('clientsecret.info.refresh_secret')} {props.clientId}?
                    </Typography>
                  )}

                  {refreshedSecret && (
                    <>
                      <Typography variant="h6">
                        {t('clientsecret.label.api_key')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: 'break-word',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {refreshedSecret.ClientId}
                        <CopyToClipboard
                          text={refreshedSecret.ClientId ?? ''}
                          onCopy={() =>
                            successToastMessage(
                              t('clientsecret.success.apikey_copied'),
                            )
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={t('clientsecret.aria.copy_client_id')}
                          >
                            <ThemedCopyIcon fontSize="small" />
                          </IconButton>
                        </CopyToClipboard>
                      </Typography>
                    </>
                  )}

                  {refreshedSecret && (
                    <>
                      <Typography variant="h6" sx={{ mt: 2 }}>
                        {t('clientsecret.label.secret')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: 'break-word',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {refreshedSecret.Secret}
                        <CopyToClipboard
                          text={refreshedSecret.Secret ?? ''}
                          onCopy={() =>
                            successToastMessage(
                              t('clientsecret.success.secret_copied'),
                            )
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={t('clientsecret.aria.copy_secret')}
                          >
                            <ThemedCopyIcon fontSize="small" />
                          </IconButton>
                        </CopyToClipboard>
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </CardWrapper>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions>
        {!refreshedSecret && (
          <CustomButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            startIcon={
              isRefreshing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ThumbUpIcon />
              )
            }
            label={t('clientsecret.button.yes')}
          />
        )}

        <CustomButton
          onClick={handleClose}
          startIcon={refreshedSecret ? <Cancel /> : <ThumbDownIcon />}
          label={
            refreshedSecret
              ? t('clientsecret.button.close')
              : t('clientsecret.button.no')
          }
        />
      </DialogActions>

      <Backdrop
        open={isRefreshing}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}
